import type { Snapshot } from '@/api/setup-chat'
import type { Plan } from '@/api/setup-runs'
import { Button } from '@/components/ui/button'
import { mailboxWords } from '@/lib/mailbox-words'

type Approval = Snapshot['approvals'][number]

/** What the plan says about new mail; kept in one place while it's being decided. */
function newMailLine(plan: Plan): string {
  const sorting = plan.inboxes.filter((inbox) => inbox.sorts_new_mail)
  if (sorting.length === 0) return 'New mail stays as it is until you say so.'
  const many = mailboxWords(sorting.map((inbox) => inbox.kind)).many
  if (sorting.length === plan.inboxes.length)
    return `New mail gets these ${many} too.`
  return `New mail in ${sorting.map((inbox) => inbox.name).join(' and ')} gets these ${many} too.`
}

/** What else the run may do: the inboxes' saved rules apply to these emails too. */
function savedRulesLine(plan: Plan, many: string): string {
  const read = plan.inboxes.some((inbox) => inbox.marks_read)
  const archive = plan.inboxes.some((inbox) => inbox.archives)
  if (!read && !archive)
    return `Adds ${many} only. It doesn't archive anything or mark it read.`
  const what = read && archive ? 'marked read or archived' : read ? 'marked read' : 'archived'
  return `Your saved rules apply too, so some emails may also be ${what}.`
}

function where(plan: Plan, inboxIds: string[]): string {
  if (plan.inboxes.length > 1 && inboxIds.length === plan.inboxes.length)
    return plan.inboxes.length === 2
      ? 'Both inboxes'
      : `All ${plan.inboxes.length} inboxes`
  return plan.inboxes
    .filter((inbox) => inboxIds.includes(inbox.inbox_id))
    .map((inbox) => inbox.name)
    .join(', ')
}

function newLabels(plan: Plan): string {
  const words = mailboxWords(plan.inboxes.map((inbox) => inbox.kind))
  const count = plan.labels.filter((label) => label.new).length
  if (count === 0) return `Uses ${words.many} you already have`
  const noun = `new ${count === 1 ? words.one : words.many}`
  if (plan.inboxes.length === 1) return `${count} ${noun}`
  return `${count} ${noun} across ${plan.inboxes.length === 2 ? 'both' : 'your'} inboxes`
}

const STATUS: Partial<Record<Approval['status'], string>> = {
  approved: 'Starting…',
  denied: 'Not now',
  expired: 'This plan expired. Ask again for a fresh one.',
  stale: 'Your setup changed since this plan. Ask again for a fresh one.',
  unknown: "We couldn't confirm whether this finished. Check your mail before trying again.",
}

export function PlanCard({
  approval,
  disabled,
  onDecide,
  now,
}: {
  approval: Approval
  disabled: boolean
  onDecide: (decision: 'approve' | 'deny') => void
  /** The chat's clock, for the expiry check. */
  now: number
}) {
  const plan = approval.plan
  if (!plan) return null
  const open =
    approval.status === 'pending' && Date.parse(approval.expires_at) > now
  const status =
    approval.status === 'pending' && !open
      ? STATUS.expired
      : STATUS[approval.status]
  const words = mailboxWords(plan.inboxes.map((inbox) => inbox.kind))
  const emails = (count: number) =>
    `${count.toLocaleString()} ${count === 1 ? 'email' : 'emails'}`
  // "1 email returns" but "2 emails return".
  const verb = (count: number, one: string, many: string) =>
    count === 1 ? one : many
  const earlier = [
    plan.up_to_inbox > 0 &&
      `up to ${emails(plan.up_to_inbox)} ${verb(plan.up_to_inbox, 'returns', 'return')} to your inbox`,
    ...Object.entries(plan.up_to_unlabel ?? {}).map(
      ([name, count]) =>
        `up to ${emails(count)} ${verb(count, 'loses', 'lose')} the ${name} ${words.one}`,
    ),
    plan.up_to_unread > 0 &&
      `up to ${emails(plan.up_to_unread)} ${verb(plan.up_to_unread, 'becomes', 'become')} unread`,
  ].filter((line): line is string => Boolean(line))
  const changes = [
    plan.mark_read_rule && `Marks read: ${plan.mark_read_rule}`,
    plan.archive_rule && `Moves out of the inbox: ${plan.archive_rule}`,
  ].filter(Boolean)
  return (
    <div className="min-w-0 rounded-xl border bg-card p-4">
      <h3 className="font-semibold">{plan.title}</h3>
      <p className="text-sm text-muted-foreground">{newLabels(plan)}</p>
      <ul className="mt-3 divide-y">
        {plan.labels.map((label) => (
          <li
            key={label.name}
            className="flex min-w-0 flex-col items-start gap-1 py-2.5 first:pt-0.5"
          >
            <span className="max-w-full rounded-md bg-accent px-2 py-0.5 text-xs font-semibold break-words text-accent-foreground">
              {label.name}
            </span>
            <span className="text-sm break-words">{label.rule}</span>
            <span className="max-w-full truncate text-xs text-muted-foreground">
              {where(plan, label.inbox_ids)}
            </span>
          </li>
        ))}
      </ul>
      <div className="mt-3 space-y-1 rounded-lg bg-muted px-3 py-2.5 text-sm">
        <p>
          Run it on{' '}
          <span className="font-semibold">
            {plan.capped ? 'the newest ' : ''}
            {emails(plan.total)}
          </span>{' '}
          from the last 30 days
        </p>
        {changes.length ? (
          changes.map((change) => <p key={change}>{change}</p>)
        ) : (
          <p>{savedRulesLine(plan, words.many)}</p>
        )}
        <p>{newMailLine(plan)}</p>
      </div>
      {earlier.length > 0 && (
        <div className="mt-2 rounded-lg bg-muted px-3 py-2.5 text-sm">
          <p>
            {plan.earlier_sorted.toLocaleString()} of these{' '}
            {verb(plan.earlier_sorted, 'was', 'were')} sorted by your earlier
            rules. Anything the new rules wouldn&apos;t do is put back:
          </p>
          <ul className="mt-1 list-disc pl-5">
            {earlier.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}
      {open ? (
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button
            variant="outline"
            disabled={disabled}
            onClick={() => onDecide('deny')}
          >
            Not now
          </Button>
          <Button disabled={disabled} onClick={() => onDecide('approve')}>
            Create and run
          </Button>
        </div>
      ) : (
        status && (
          <p className="mt-3 text-sm text-muted-foreground">{status}</p>
        )
      )}
    </div>
  )
}
